import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

/** Client row from /api/clients (search or recent). */
export interface Client {
  id: string | number;
  name?: string | null;
  phone?: string | null;
  total_visits?: number | null;
  total_spent?: number | null;
  favorite_quest?: string | null;
  last_visit?: string | null;
}

export type ClientSort = "recent" | "games" | "money";

/** ISO yyyy-mm-dd range to aggregate clients over bookings. */
export interface ClientsRange {
  from: string;
  to: string;
}

type Resp = Client[] | { results?: Client[] };

export type SortDir = "asc" | "desc";

interface UseClientsOpts {
  sort?: ClientSort;
  /** Sort direction — toggled by a second tap on the active sort chip. */
  dir?: SortDir;
  /** When set, list = clients with bookings in [from, to]; metrics are in-range. */
  range?: ClientsRange | null;
  /** Тип-фильтр (server-side по всей базе): "games"|"bookings"|"edits", через запятую. */
  flt?: string;
  pageSize?: number;
}

function buildPath(q: string, sort: ClientSort, dir: SortDir, range: ClientsRange | null, flt: string, limit: number, offset: number): string {
  if (q) return `/api/clients?q=${encodeURIComponent(q)}`;
  const p = new URLSearchParams({ limit: String(limit), offset: String(offset), sort, dir });
  if (range) { p.set("from", range.from); p.set("to", range.to); }
  if (flt) p.set("flt", flt);
  return `/api/clients?${p.toString()}`;
}

const norm = (r: Resp): Client[] => (Array.isArray(r) ? r : r.results ?? []);

/**
 * Client list with append pagination. Empty query → recent/sorted/range list.
 *
 * Key behaviour: `loadMore()` fetches the next page and APPENDS it without
 * touching `loading` (it sets `loadingMore` instead), so the rendered list is
 * never unmounted and scroll position is preserved — the «Показать ещё» button
 * doesn't reload the screen. Changing query/sort/range resets to page 0.
 */
export function useClients(query: string, opts: UseClientsOpts = {}) {
  const sort = opts.sort ?? "recent";
  const dir = opts.dir ?? "desc";
  const range = opts.range ?? null;
  const flt = opts.flt ?? "";
  const pageSize = opts.pageSize ?? 50;

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const q = query.trim();
  const rangeKey = range ? `${range.from}|${range.to}` : "";

  /* Latest params + count for loadMore without re-creating the callback. */
  const paramsRef = useRef({ q, sort, dir, range, flt, pageSize, count: 0 });
  paramsRef.current = { q, sort, dir, range, flt, pageSize, count: clients.length };
  const fetchingRef = useRef(false);

  /* Fresh load (and reset) on query / sort / range change. */
  useEffect(() => {
    let alive = true;
    const handle = setTimeout(() => {
      setLoading(true);
      setError(null);
      api<Resp>(buildPath(q, sort, dir, range, flt, pageSize, 0))
        .then((r) => {
          if (!alive) return;
          const list = norm(r);
          setClients(list);
          setHasMore(!q && list.length >= pageSize);
          setLoading(false);
        })
        .catch((e: unknown) => {
          if (!alive) return;
          setError(e instanceof Error ? e.message : "Не удалось загрузить клиентов");
          setLoading(false);
        });
    }, q ? 300 : 0);
    return () => { alive = false; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, sort, dir, rangeKey, flt, pageSize]);

  const loadMore = useCallback(() => {
    const { q: cq, sort: cs, dir: cd, range: cr, flt: cf, pageSize: cp, count } = paramsRef.current;
    if (cq || fetchingRef.current) return;
    fetchingRef.current = true;
    setLoadingMore(true);
    api<Resp>(buildPath(cq, cs, cd, cr, cf, cp, count))
      .then((r) => {
        const list = norm(r);
        setClients((cur) => [...cur, ...list]);
        setHasMore(list.length >= cp);
      })
      .catch(() => {})
      .finally(() => { fetchingRef.current = false; setLoadingMore(false); });
  }, []);

  return { clients, loading, loadingMore, error, hasMore, loadMore };
}
